import React, { useState, FC } from 'react';

import { useQuery, useMutation } from '@apollo/react-hooks';
import { Box, DialogContent, Modal, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';

import { Loader } from '../../../../components/Loader';
import { CustomButton } from '../../../../components/UI/CustomButton';
import {
  GET_TEAMS_LIST,
  CREATE_TEAM,
  UPDATE_TEAM,
  DELETE_TEAM,
  UPDATE_MEMBER_ROLE,
} from '../../../../graphql/user';
import {
  Mutation,
  Query,
  TeamRole,
  TeamCreateInput,
  TeamUpdateInput,
  TeamDeleteInput,
  TeamMemberRole,
} from '../../../../types';
import { multiLangCharRegexp } from '../../../../utils/regexps';
import AllTeamsTableRow from '../AllTeamsTableRow';
import { CardSection } from '../CardSection';
import { CardTitle } from '../CardTitle';
import CreateTeamModal from '../CreateTeamModal';
import EditTeamModal from '../EditTeamModal';
import RemoveTeamModal from '../RemoveTeamModal';
import useStyles from './styles';

interface AllTeamViewProps {
  ownerId: string;
  isOwner: boolean;
}

const AllTeamView: FC<AllTeamViewProps> = ({ ownerId, isOwner }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [openCreateTeamModal, setOpenCreateTeamModal] = useState(false);
  const [openEditTeamModal, setOpenEditTeamModal] = useState(false);
  const [openRemoveTeamModal, setOpenRemoveTeamModal] = useState(false);

  const [name, setName] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [teamId, setTeamId] = useState('');
  const [alert, setAlert] = useState('');

  const { data, loading, refetch: refetchTeamsList } = useQuery<Pick<Query, 'getTeamsList'>>(
    GET_TEAMS_LIST,
  );
  const teams = data?.getTeamsList || [];

  const [createTeam] = useMutation<Pick<Mutation, 'createTeam'>, TeamCreateInput>(CREATE_TEAM, {
    onCompleted: () => {
      refetchTeamsList();
      setOpenCreateTeamModal(false);
      setAlert('alert.newTeamCreated');
      setName('');
    },
    variables: { input: { name } },
  });

  const [updateTeam] = useMutation<Pick<Mutation, 'updateTeam'>, TeamUpdateInput>(UPDATE_TEAM, {
    onCompleted: () => {
      refetchTeamsList();
      setOpenEditTeamModal(false);
      setAlert('alert.teamNameChanged');
      setTeamId('');
      setName('');
    },
    variables: { input: { name, team_id: teamId } },
  });

  const [deleteTeam] = useMutation<Pick<Mutation, 'deleteTeam'>, TeamDeleteInput>(DELETE_TEAM, {
    onCompleted: () => {
      refetchTeamsList();
      setOpenRemoveTeamModal(false);
      setAlert('alert.teamRemoved');
      setTeamId('');
      setName('');
    },
    variables: { input: { team_id: teamId } },
  });

  const [updateMemberRole] = useMutation<Pick<Mutation, 'updateMemberRole'>, TeamMemberRole>(
    UPDATE_MEMBER_ROLE,
    {
      onCompleted: () => {
        refetchTeamsList();
        setAlert('alert.teamRoleUpdated');
      },
    },
  );

  const onCreateTeam = () => {
    if (validateName()) {
      createTeam();
    }
  };

  const onUpdateTeam = () => {
    if (validateName()) {
      updateTeam();
    }
  };

  const onBecomeAManager = (teamId) => {
    updateMemberRole({
      variables: {
        input: {
          team_id: teamId,
          member_id: ownerId,
          member_role: TeamRole.Manager,
        },
      },
    });
  };

  const onOpenEditModal = (name, teamId) => {
    setName(name);
    setTeamId(teamId);
    setOpenEditTeamModal(true);
  };

  const onOpenDeleteModal = (name, teamId) => {
    setName(name);
    setTeamId(teamId);
    setOpenRemoveTeamModal(true);
  };

  const onChangeName = (value) => {
    if (value) {
      setNameErr('');
    }
    setName(value);
  };

  const validateName = () => {
    let isValid = true;
    if (!name.trim()) {
      setNameErr(t('error.fieldRequired'));
      isValid = false;
    }
    if (!!name.trim() && !multiLangCharRegexp.test(name.trim())) {
      setNameErr(t('error.incorrectCompanyName'));
      isValid = false;
    }
    return isValid;
  };

  const hideAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert('');
  };

  if (loading) return <Loader />;

  return (
    <>
      <Box>
        <CardTitle title={t('page.account.myTeams')} titleClass={classes.title} />
        <CardSection
          title={t('page.account.teamsOverview')}
          text={[t('page.account.ownerTeamsDescription')]}
          classes={{
            content: classes.container,
            textWrapper: classes.textWrap,
            title: classes.cardTitle,
          }}
        >
          <Box className={classes.tableWrap}>
            {!!teams?.length && (
              <Box className={classes.table}>
                {teams.map((team) => (
                  <AllTeamsTableRow
                    isOwner={isOwner}
                    key={team?.id}
                    team={team}
                    onEdit={() => onOpenEditModal(team?.name, team?.id)}
                    onRemove={() => onOpenDeleteModal(team?.name, team?.id)}
                    onUpdateRole={() => onBecomeAManager(team?.id)}
                  />
                ))}
              </Box>
            )}
            {isOwner && (
              <CustomButton btnClass={classes.button} onClick={() => setOpenCreateTeamModal(true)}>
                {t('button.addTeam')}
              </CustomButton>
            )}
          </Box>
        </CardSection>
      </Box>

      {openCreateTeamModal && (
        <Modal open={openCreateTeamModal} className={classes.modal}>
          <DialogContent className={classes.dialog}>
            <CreateTeamModal
              name={name}
              nameErr={nameErr}
              setName={onChangeName}
              onClick={onCreateTeam}
              setLeaveModal={() => setOpenCreateTeamModal(false)}
            />
          </DialogContent>
        </Modal>
      )}
      {openEditTeamModal && (
        <Modal open={openEditTeamModal} className={classes.modal}>
          <DialogContent className={classes.dialog}>
            <EditTeamModal
              name={name}
              nameErr={nameErr}
              setName={onChangeName}
              onClick={onUpdateTeam}
              setLeaveModal={() => setOpenEditTeamModal(false)}
            />
          </DialogContent>
        </Modal>
      )}
      {openRemoveTeamModal && (
        <Modal open={openRemoveTeamModal} className={classes.modal}>
          <DialogContent className={classes.dialog}>
            <RemoveTeamModal
              name={name}
              onClick={deleteTeam}
              setLeaveModal={() => setOpenRemoveTeamModal(false)}
            />
          </DialogContent>
        </Modal>
      )}

      <Snackbar open={!!alert} autoHideDuration={3000} onClose={hideAlert}>
        <MuiAlert elevation={6} variant="filled" onClose={hideAlert} severity="success">
          {t(alert)}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AllTeamView;
